import { useSelector } from 'react-redux'
import type { AgentStreamPayload, ChatMessageStream, ToolCallSummary } from '@tunasong/redux'
import { nlpApi } from '@tunasong/redux'

/**
 * Projection returned by {@link useInFlightAgentStream}. The sidebar consumer
 * renders the streamed content as a synthetic feed item below the persisted
 * comments while `pending: true`, and keeps rendering it until either the
 * persisted comment arrives (replacing the item) or the cache entry evicts.
 */
export interface InFlightAgentStream {
  /** RTK Query cache key — used as the React `key` of the synthetic item. */
  requestId: string
  /** Accumulated text content (from `text-delta` chunks). */
  content: string
  /** Tool calls projected from the parts stream, in invocation order. */
  toolCalls: ToolCallSummary[]
  /** True while the stream is still arriving. */
  pending: boolean
  /** Terminal error message, if the fetch failed. */
  error?: string
}

interface QueryEntry {
  endpointName?: string
  originalArgs?: AgentStreamPayload
  startedTimeStamp?: number
  data?: ChatMessageStream | null
}

const isAgentStreamEntry = (entry: unknown): entry is QueryEntry => {
  if (typeof entry !== 'object' || entry === null) {
    return false
  }
  const candidate = entry as QueryEntry
  return candidate.endpointName === 'agentStream' && typeof candidate.originalArgs?.agentId === 'string'
}

/**
 * Subscribes to the `agentStream` RTK Query cache and returns the most-recent
 * entry whose `originalArgs.agentId` matches `agentId`. Returns `null` when no
 * such entry exists.
 *
 * Lookup is by-iteration rather than by-key because the cache key includes
 * `requestId` (a per-submit nonce we don't have at read time). Iterating ~1-5
 * entries on each render is well below any perf budget.
 */
export const useInFlightAgentStream = (agentId: string | undefined): InFlightAgentStream | null =>
  useSelector(state => {
    if (!agentId) {
      return null
    }
    // The RootState typing from @tunasong/redux includes the nlp-api slice via
    // `[nlpApi.reducerPath]` but the structural cast keeps this hook portable
    // for tests that mount a smaller store.
    const slice = (state as Record<string, unknown>)[nlpApi.reducerPath] as
      | { queries?: Record<string, unknown> }
      | undefined
    const queries = slice?.queries
    if (!queries) {
      return null
    }

    let best: QueryEntry | null = null
    for (const value of Object.values(queries)) {
      if (!isAgentStreamEntry(value)) {
        continue
      }
      if (value.originalArgs?.agentId !== agentId) {
        continue
      }
      if (best === null) {
        best = value
        continue
      }
      const a = value.startedTimeStamp ?? 0
      const b = best.startedTimeStamp ?? 0
      if (a > b) {
        best = value
      }
    }

    if (!best || !best.originalArgs) {
      return null
    }

    const data = best.data ?? null
    return {
      requestId: best.originalArgs.requestId,
      content: data?.content ?? '',
      toolCalls: data?.toolCalls ?? [],
      pending: data?.pending ?? true,
      error: data?.error,
    } satisfies InFlightAgentStream
  })
